import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import PromoBanner from "@/components/PromoBanner";
import LeagueManager from "@/components/LeagueManager";
import Dashboard from "@/components/Dashboard";
import SharedTrade from "@/components/SharedTrade";
import SleeperLogin from "@/components/SleeperLogin";
import AuthCallback from "@/components/AuthCallback";
import LandingPage from "@/components/LandingPage";
import { TermsOfService, PrivacyPolicy } from "@/components/LegalPages";
import { EmailPreferences, ReferralPage } from "@/components/UserSettings";
import SiteFooter from "@/components/SiteFooter";
import OnboardingTour from "@/components/OnboardingTour";

// Lazy-load heavy components (only loaded when navigated to)
const TradeAnalyzer = lazy(() => import("@/components/TradeAnalyzer"));
const TradeCalculator = lazy(() => import("@/components/TradeCalculator"));
const TradeInbox = lazy(() => import("@/components/TradeInbox"));
const PlayerCompare = lazy(() => import("@/components/PlayerCompare"));
const AllTeams = lazy(() => import("@/components/AllTeams"));
const DraftWarRoom = lazy(() => import("@/components/DraftWarRoom"));
const AdminDashboard = lazy(() => import("@/components/AdminDashboard"));
const TradeFinder = lazy(() => import("@/components/TradeFinder"));
const TargetPlayer = lazy(() => import("@/components/TargetPlayer"));
const TradeSimulator = lazy(() => import("@/components/TradeSimulator"));
const MockDraft = lazy(() => import("@/components/MockDraft"));
const RosterOptimizer = lazy(() => import("@/components/RosterOptimizer"));
const LeagueHistory = lazy(() => import("@/components/LeagueHistory"));
const TradeEvaluator = lazy(() => import("@/components/TradeEvaluator"));
const ContenderScore = lazy(() => import("@/components/ContenderScore"));
const BillingPage = lazy(() => import("@/components/BillingPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = "/api";

// Token-based auth — stores session token in localStorage and sends via Authorization header
// This bypasses cookie/CORS issues from the platform proxy
const TOKEN_KEY = "dynastydeal_session_token";
export function getAuthToken() { return localStorage.getItem(TOKEN_KEY); }
export function setAuthToken(token) { if (token) localStorage.setItem(TOKEN_KEY, token); }
export function clearAuthToken() { localStorage.removeItem(TOKEN_KEY); }

// Axios interceptor: add Authorization header to all API requests
axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && config.url?.startsWith("/api")) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export { API };

// Error boundary to catch crashes and show fallback instead of black screen
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Dashboard crash:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <p className="text-[#ff6b6b] text-lg font-bold mb-2">Something went wrong</p>
          <p className="text-[#a3a3a3] text-sm mb-4">{this.state.error?.message || "The dashboard encountered an error."}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }} className="h-10 px-6 bg-[#ccff00] text-black font-bold text-sm rounded-sm">RELOAD</button>
        </div>
      );
    }
    return this.props.children;
  }
}


// Analytics tracking helper
export function trackEvent(event, details = "", userId = "") {
  try {
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, details, user_id: userId }),
    }).catch(() => {});
  } catch (e) { /* silent */ }
}

function LazyFallback() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="w-5 h-5 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin" />
      <p className="text-xs text-[#666]">Loading...</p>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect auth_token or session_id or auth error synchronously during render
  if (location.hash?.includes('auth_token=') || location.hash?.includes('session_id=') || location.hash?.includes('auth_error=')) {
    return <AuthCallbackWrapper />;
  }

  return <MainApp />;
}

function AuthCallbackWrapper() {
  const navigate = useNavigate();
  const [, setUser] = useState(null);

  const handleAuth = useCallback(async (userData) => {
    localStorage.setItem("dynastydeal_user", JSON.stringify(userData));
    setUser(userData);
    // Track referral if present
    const refCode = localStorage.getItem('dynastydeal_referral');
    if (refCode && userData?.user_id) {
      try {
        await axios.post(`${API}/referral/track`, { code: refCode, user_id: userData.user_id });
        localStorage.removeItem('dynastydeal_referral');
      } catch (e) { /* ignore */ }
    }
    navigate("/", { replace: true });
    window.location.reload();
  }, [navigate]);

  return <AuthCallback onAuth={handleAuth} />;
}

function MainApp() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [activeLeague, setActiveLeague] = useState(null);
  const [leagueData, setLeagueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [myRosterId, setMyRosterId] = useState(null);
  const [teamSelections, setTeamSelections] = useState({});
  const [authLoading, setAuthLoading] = useState(true);
  const [sleeperReady, setSleeperReady] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [credits, setCredits] = useState(null);
  const lastLeagueLoadRef = useRef(0);

  // Fetch credits
  const fetchCredits = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/credits`);
      setCredits(res.data);
    } catch (e) { /* not critical */ }
  }, []);

  // Track referral code from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('dynastydeal_referral', ref);
    }
  }, []);

  // Check auth on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('auth_token=') || window.location.hash?.includes('session_id=') || window.location.hash?.includes('auth_error=')) {
      setAuthLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        const googleUser = res.data;
        
        // If Google user doesn't have Sleeper linked, check localStorage for guest data
        if (!googleUser.sleeper_username) {
          const saved = localStorage.getItem("dynastydeal_user");
          if (saved) {
            const guest = JSON.parse(saved);
            if (guest.sleeper_username) {
              try {
                const linkRes = await axios.post(`${API}/auth/link-sleeper`, { username: guest.sleeper_username });
                googleUser.sleeper_username = linkRes.data.sleeper_username;
                googleUser.sleeper_user_id = linkRes.data.sleeper_user_id;
              } catch (e) { /* linking failed */ }
            }
          }
        }
        
        // If Sleeper is linked, auto-discover and import leagues
        if (googleUser.sleeper_username && googleUser.sleeper_user_id) {
          try {
            const leaguesRes = await axios.get(`${API}/auth/leagues/${googleUser.sleeper_user_id}`);
            const discoveredLeagues = leaguesRes.data || [];
            // Import any leagues not already saved for this user
            for (const lg of discoveredLeagues) {
              try {
                await axios.post(`${API}/leagues`, { league_id: lg.league_id, user_id: googleUser.user_id });
                if (lg.my_roster_id) {
                  await axios.put(`${API}/leagues/${lg.league_id}/my-team`, { my_roster_id: lg.my_roster_id });
                }
              } catch (e) { /* already exists */ }
            }
          } catch (e) { /* discovery failed, user can import manually */ }
        }
        
        setUser(googleUser);
        localStorage.setItem("dynastydeal_user", JSON.stringify(googleUser));
        setShowLanding(false);
        if (googleUser.sleeper_username) { setSleeperReady(true); }
      } catch (e) {
        // Check localStorage fallback
        const saved = localStorage.getItem("dynastydeal_user");
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setUser(parsed);
            setShowLanding(false);
            if (parsed.sleeper_username) { setSleeperReady(true); }
          } catch (err) {
            localStorage.removeItem("dynastydeal_user");
          }
        }
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) { /* ignore */ }
    setUser(null);
    setLeagues([]);
    setActiveLeague(null);
    setLeagueData(null);
    setMyRosterId(null);
    setSleeperReady(false);
    localStorage.removeItem("dynastydeal_user");
    clearAuthToken();
  };

  const handleSleeperLinked = (userData) => {
    if (userData) {
      setUser(prev => ({ ...prev, ...userData }));
      localStorage.setItem("dynastydeal_user", JSON.stringify(userData));
    }
    setSleeperReady(true);
    setShowLanding(false);
  };

  const handleGuestSleeper = (sleeperData) => {
    const guestUser = {
      user_id: `guest_${sleeperData.sleeper_user_id}`,
      name: sleeperData.display_name || sleeperData.sleeper_username,
      sleeper_username: sleeperData.sleeper_username,
      sleeper_user_id: sleeperData.sleeper_user_id,
      sleeper_avatar: sleeperData.avatar,
      is_guest: true,
    };
    setUser(guestUser);
    localStorage.setItem("dynastydeal_user", JSON.stringify(guestUser));
    setSleeperReady(true);
    setShowLanding(false);
  };

  const fetchLeagues = useCallback(async () => {
    if (!user) return;
    const uid = user.user_id || user.sleeper_user_id || "";
    if (!uid) return; // Never fetch without a user ID
    try {
      const res = await axios.get(`${API}/leagues?user_id=${encodeURIComponent(uid)}`);
      setLeagues(res.data || []);
      const selections = {};
      for (const l of (res.data || [])) {
        if (l.my_roster_id) selections[l.league_id] = l.my_roster_id;
      }
      setTeamSelections(selections);
    } catch (e) {
      console.error("Failed to fetch leagues", e);
      setLeagues([]);
    }
  }, [user]);

  useEffect(() => {
    if (sleeperReady) { fetchLeagues(); fetchCredits(); }
  }, [sleeperReady, fetchLeagues, fetchCredits]);

  const loadLeagueData = useCallback(async (leagueId, opts = {}) => {
    if (!opts.silent) {
      setLoading(true);
      setLeagueData(null);
    }
    try {
      const res = await axios.get(`${API}/sleeper/league/${leagueId}/full`);
      if (res.data && res.data.rosters) {
        lastLeagueLoadRef.current = Date.now();
        setLeagueData(res.data);
        setActiveLeague(leagueId);
        const savedRosterId = teamSelections[leagueId];
        if (savedRosterId) {
          setMyRosterId(savedRosterId);
        } else {
          // Find the user's own roster by matching sleeper_user_id to owner_id
          const sleeperUid = user?.sleeper_user_id || "";
          const myRoster = sleeperUid
            ? res.data.rosters.find(r => r.owner_id === sleeperUid || String(r.owner_id) === String(sleeperUid))
            : null;
          setMyRosterId(myRoster ? myRoster.roster_id : res.data.rosters[0]?.roster_id);
        }
      }
    } catch (e) {
      console.error("Failed to load league data", e);
      if (!opts.silent) setLeagueData(null);
      if (opts.silent) throw e;
    } finally {
      setLoading(false);
    }
  }, [teamSelections]);

  // Auto re-pull league when user returns after a few hours away
  useEffect(() => {
    const STALE_MS = 3 * 60 * 60 * 1000;
    const maybeRefresh = () => {
      if (document.visibilityState !== "visible") return;
      if (!activeLeague || !lastLeagueLoadRef.current) return;
      if (Date.now() - lastLeagueLoadRef.current < STALE_MS) return;
      lastLeagueLoadRef.current = Date.now();
      loadLeagueData(activeLeague, { silent: true }).catch(() => {});
    };
    document.addEventListener("visibilitychange", maybeRefresh);
    window.addEventListener("focus", maybeRefresh);
    return () => {
      document.removeEventListener("visibilitychange", maybeRefresh);
      window.removeEventListener("focus", maybeRefresh);
    };
  }, [activeLeague, loadLeagueData]);

  const addLeague = async (leagueId) => {
    try {
      await axios.post(`${API}/leagues`, { league_id: leagueId, user_id: user?.user_id || user?.sleeper_user_id || "" });
      await fetchLeagues();
      await loadLeagueData(leagueId);
      return true;
    } catch (e) { return false; }
  };

  const removeLeague = async (leagueId) => {
    try {
      await axios.delete(`${API}/leagues/${leagueId}?user_id=${user?.user_id || user?.sleeper_user_id || ""}`);
      await fetchLeagues();
      if (activeLeague === leagueId) { setActiveLeague(null); setLeagueData(null); }
    } catch (e) { console.error("Failed to remove league", e); }
  };

  const updateMyRosterId = useCallback(async (rosterId) => {
    setMyRosterId(rosterId);
    if (activeLeague) {
      setTeamSelections(prev => ({ ...prev, [activeLeague]: rosterId }));
      try { await axios.put(`${API}/leagues/${activeLeague}/my-team`, { my_roster_id: rosterId }); } catch (e) {}
    }
  }, [activeLeague]);

  const sharedProps = {
    user, leagues, activeLeague, leagueData, loading, myRosterId,
    setMyRosterId: updateMyRosterId, loadLeagueData, addLeague, removeLeague, setActiveLeague, handleLogout,
    credits, refreshCredits: fetchCredits,
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Legal pages accessible without any auth
  const publicPaths = ['/terms', '/privacy', '/email-preferences', '/how-it-works', '/features', '/faq', '/guide', '/guide/superflex-strategy', '/guide/sleeper-setup', '/guide/startup-draft-strategy', '/guide/dynasty-startup-draft', '/guide/dynasty-vs-redraft', '/compare/dynasty-trade-value-tools', '/features/trade-analyzer', '/features/trade-finder', '/features/draft-room', '/features/trade-simulator', '/features/roster-tools', '/trade-calculator', '/rankings', '/rookie-rankings', '/championship-blueprint', '/research', '/research/championship-report', '/research/qb-question', '/research/league-winners', '/research/stars-and-scrubs', '/research/age-of-champion', '/research/elite-te-question', '/research/positional-blueprint'];
  const isPublicPath = publicPaths.includes(location.pathname) || location.pathname.startsWith('/guide/') || location.pathname.startsWith('/compare') || location.pathname.startsWith('/blog') || location.pathname.startsWith('/players') || location.pathname.startsWith('/research/') || location.pathname.startsWith('/trades') || location.pathname.startsWith('/weekly') || location.pathname.startsWith('/tools') || location.pathname === '/pricing' || location.pathname === '/dynasty50' || location.pathname === '/auth/complete' || location.pathname === '/about' || location.pathname === '/contact' || location.pathname === '/unsubscribe' || location.pathname === '/sleeper-analyzer' || location.pathname.startsWith('/sitemap');
  if (isPublicPath && !sleeperReady) {
    const HowItWorks = require("@/components/HowItWorks").default;
    const FeaturesPage = require("@/components/FeaturesPage").default;
    const FaqPage = require("@/components/FaqPage").default;
    const GuidePage = require("@/components/GuidePage").default;
    const SuperflexGuide = require("@/components/content/SuperflexGuide").default;
    const SleeperSetupGuide = require("@/components/content/SleeperSetupGuide").default;
    const StartupDraftGuide = require("@/components/content/StartupDraftGuide").default;
    const DynastyVsRedraft = require("@/components/content/DynastyVsRedraft").default;
    const TradeValueToolsComparison = require("@/components/content/TradeValueToolsComparison").default;
    const TradeAnalyzerFeature = require("@/components/features/TradeAnalyzerFeature").default;
    const TradeFinderFeature = require("@/components/features/TradeFinderFeature").default;
    const DraftRoomFeature = require("@/components/features/DraftRoomFeature").default;
    const TradeSimulatorFeature = require("@/components/features/TradeSimulatorFeature").default;
    const RosterToolsFeature = require("@/components/features/RosterToolsFeature").default;
    return <>
      <Routes>
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/email-preferences" element={<EmailPreferences user={user} />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/pricing" element={(() => { const C = require("@/components/PricingPage").default; return <C />; })()} />
        <Route path="/dynasty50" element={(() => { const C = require("@/components/PromoPage").default; return <C />; })()} />
        <Route path="/about" element={(() => { const C = require("@/components/content/AboutPage").default; return <C />; })()} />
        <Route path="/contact" element={(() => { const C = require("@/components/content/ContactPage").default; return <C />; })()} />
        <Route path="/sleeper-analyzer" element={(() => { const C = require("@/components/content/SleeperAnalyzerPage").default; return <C />; })()} />
        <Route path="/sitemap" element={(() => { const { SitemapIndex } = require("@/components/content/SitemapPages"); return <SitemapIndex />; })()} />
        <Route path="/sitemap/players" element={(() => { const { SitemapPlayers } = require("@/components/content/SitemapPages"); return <SitemapPlayers />; })()} />
        <Route path="/sitemap/trades" element={(() => { const { SitemapTrades } = require("@/components/content/SitemapPages"); return <SitemapTrades />; })()} />
        <Route path="/sitemap/compare" element={(() => { const { SitemapCompare } = require("@/components/content/SitemapPages"); return <SitemapCompare />; })()} />
        <Route path="/sitemap/compare/:page" element={(() => { const { SitemapCompare } = require("@/components/content/SitemapPages"); return <SitemapCompare />; })()} />
        <Route path="/unsubscribe" element={(() => { const C = require("@/components/content/UnsubscribePage").default; return <C />; })()} />
        <Route path="/auth/complete" element={(() => { const C = require("@/components/AuthCompletePage").default; return <C />; })()} />
        <Route path="/report/:reportId" element={(() => { const C = require("@/components/SharedReport").default; return <C />; })()} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/guide/superflex-strategy" element={<SuperflexGuide />} />
        <Route path="/guide/sleeper-setup" element={<SleeperSetupGuide />} />
        <Route path="/guide/startup-draft-strategy" element={<StartupDraftGuide />} />
        <Route path="/guide/dynasty-startup-draft" element={(() => { const C = require("@/components/content/StartupDraftDataGuide").default; return <C />; })()} />
        <Route path="/guide/dynasty-trade-deadline" element={(() => { const C = require("@/components/content/TradeDeadlineGuide").default; return <C />; })()} />
        <Route path="/guide/dynasty-rookie-draft" element={(() => { const C = require("@/components/content/RookieDraftGuide").default; return <C />; })()} />
        <Route path="/guide/dynasty-rebuild" element={(() => { const C = require("@/components/content/RebuildGuide").default; return <C />; })()} />
        <Route path="/guide/dynasty-vs-redraft" element={<DynastyVsRedraft />} />
        <Route path="/compare/dynasty-trade-value-tools" element={<TradeValueToolsComparison />} />
        <Route path="/features/trade-analyzer" element={<TradeAnalyzerFeature />} />
        <Route path="/features/trade-finder" element={<TradeFinderFeature />} />
        <Route path="/features/draft-room" element={<DraftRoomFeature />} />
        <Route path="/features/trade-simulator" element={<TradeSimulatorFeature />} />
        <Route path="/features/roster-tools" element={<RosterToolsFeature />} />
        <Route path="/trade-calculator" element={(() => { const PublicCalculator = require("@/components/PublicCalculator").default; return <PublicCalculator />; })()} />
        <Route path="/rankings" element={(() => { const DynastyValueChart = require("@/components/content/DynastyValueChart").default; return <DynastyValueChart />; })()} />
        <Route path="/rookie-rankings" element={(() => { const RookieRankings = require("@/components/content/RookieRankings").default; return <RookieRankings />; })()} />
        <Route path="/guide/:slug" element={(() => { const GuideArticle = require("@/components/content/GuideArticle").default; return <GuideArticle />; })()} />
        <Route path="/compare/dynasty-trade-value-tools" element={(() => { const C = require("@/components/content/TradeValueToolsComparison").default; return <C />; })()} />
        <Route path="/compare" element={(() => { const C = require("@/components/content/CompareIndex").default; return <C />; })()} />
        <Route path="/compare/:pair" element={(() => { const C = require("@/components/content/ComparisonPage").default; return <C />; })()} />
        <Route path="/blog" element={(() => { const { BlogList } = require("@/components/BlogPage"); return <BlogList />; })()} />
        <Route path="/blog/:slug" element={(() => { const { BlogPost } = require("@/components/BlogPage"); return <BlogPost />; })()} />
        <Route path="/players" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
        <Route path="/players/qb" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
        <Route path="/players/rb" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
        <Route path="/players/wr" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
        <Route path="/players/te" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
        <Route path="/players/:name" element={(() => { const C = require("@/components/content/PlayerPage").default; return <C />; })()} />
        <Route path="/championship-blueprint" element={(() => { const C = require("@/components/content/ChampionshipBlueprint").default; return <C />; })()} />
        <Route path="/research" element={(() => { const C = require("@/components/content/ResearchIndex").default; return <C />; })()} />
        <Route path="/research/championship-report" element={(() => { const C = require("@/components/content/ChampionshipReport").default; return <C />; })()} />
        <Route path="/research/qb-question" element={(() => { const { SpokeA_QBQuestion } = require("@/components/content/ChampionshipSpokes"); return <SpokeA_QBQuestion />; })()} />
        <Route path="/research/league-winners" element={(() => { const { SpokeB_LeagueWinners } = require("@/components/content/ChampionshipSpokes"); return <SpokeB_LeagueWinners />; })()} />
        <Route path="/research/stars-and-scrubs" element={(() => { const { SpokeC_StarsAndScrubs } = require("@/components/content/ChampionshipSpokes"); return <SpokeC_StarsAndScrubs />; })()} />
        <Route path="/research/age-of-champion" element={(() => { const { SpokeD_AgeOfChampion } = require("@/components/content/ChampionshipSpokes"); return <SpokeD_AgeOfChampion />; })()} />
        <Route path="/research/elite-te-question" element={(() => { const { SpokeF_EliteTE } = require("@/components/content/ChampionshipSpokes"); return <SpokeF_EliteTE />; })()} />
        <Route path="/research/positional-blueprint" element={(() => { const { SpokeG_PositionalBlueprint } = require("@/components/content/ChampionshipSpokes"); return <SpokeG_PositionalBlueprint />; })()} />
        <Route path="/trades" element={(() => { const { TradeExplorer } = require("@/components/content/TradeExplorer"); return <TradeExplorer />; })()} />
        <Route path="/trades/graded" element={(() => { const C = require("@/components/content/GradedTrades").default; return <C />; })()} />
        <Route path="/trades/:slug" element={(() => { const { PlayerTrades } = require("@/components/content/TradeExplorer"); return <PlayerTrades />; })()} />
        <Route path="/weekly/trades" element={(() => { const { WeeklyTrades } = require("@/components/content/WeeklyPulse"); return <WeeklyTrades />; })()} />
        <Route path="/weekly/waiver-wire" element={(() => { const { WaiverWireReport } = require("@/components/content/WeeklyPulse"); return <WaiverWireReport />; })()} />
        <Route path="/weekly/movers" element={(() => { const { ValueMovers } = require("@/components/content/WeeklyPulse"); return <ValueMovers />; })()} />
        <Route path="/tools/trade-fairness" element={(() => { const C = require("@/components/content/CollusionChecker").default; return <C />; })()} />
        <Route path="/tools/draft-archetype" element={(() => { const C = require("@/components/content/DraftArchetypeFinder").default; return <C />; })()} />
        <Route path="/research/how-lopsided-is-the-average-trade" element={(() => { const C = require("@/components/content/HowLopsidedResearch").default; return <C />; })()} />
        <Route path="/research/do-teams-that-trade-more-win" element={(() => { const C = require("@/components/content/TradeMoreWinResearch").default; return <C />; })()} />
        <Route path="/research/what-championship-rosters-have-in-common" element={(() => { const C = require("@/components/content/ChampionshipRostersResearch").default; return <C />; })()} />
        <Route path="/research/who-wins-lopsided-trades" element={(() => { const C = require("@/components/content/LopsidedTradesResearch").default; return <C />; })()} />
        <Route path="/research/what-winning-trades-look-like" element={(() => { const C = require("@/components/content/WinningTradesResearch").default; return <C />; })()} />
        <Route path="/research/how-champions-build-rosters" element={(() => { const C = require("@/components/content/ChampionsBuildResearch").default; return <C />; })()} />
        <Route path="/research/startup-strategies-that-win" element={(() => { const C = require("@/components/content/StartupStrategiesResearch").default; return <C />; })()} />
        <Route path="/research/how-long-a-rebuild-takes" element={(() => { const C = require("@/components/content/RebuildLengthResearch").default; return <C />; })()} />
        <Route path="/research/which-rookie-picks-hit" element={(() => { const C = require("@/components/content/RookieHitRatesResearch").default; return <C />; })()} />
        <Route path="/research/the-real-age-of-a-champion" element={(() => { const C = require("@/components/content/ChampionAgeResearch").default; return <C />; })()} />
        <Route path="/research/do-you-need-an-elite-qb" element={(() => { const C = require("@/components/content/EliteQBResearch").default; return <C />; })()} />
        <Route path="/research/fastest-way-out-of-a-rebuild" element={(() => { const C = require("@/components/content/FastestRebuildResearch").default; return <C />; })()} />
        <Route path="/research/stars-vs-depth" element={(() => { const C = require("@/components/content/StarsVsDepthResearch").default; return <C />; })()} />
        <Route path="/research/where-champions-put-their-value" element={(() => { const C = require("@/components/content/PositionalValueResearch").default; return <C />; })()} />
        <Route path="/research/championship-dna-players" element={(() => { const C = require("@/components/content/ChampionshipDNAResearch").default; return <C />; })()} />
        <Route path="/research/what-happens-in-startup-drafts" element={(() => { const C = require("@/components/content/StartupDraftsResearch").default; return <C />; })()} />
        <Route path="/research/how-many-studs-to-win" element={(() => { const C = require("@/components/content/HowManyStudsResearch").default; return <C />; })()} />
        <Route path="/research/do-champions-pay-up-at-tight-end" element={(() => { const C = require("@/components/content/TightEndResearch").default; return <C />; })()} />
        <Route path="/research/when-to-make-your-move" element={(() => { const C = require("@/components/content/TradeTimingResearch").default; return <C />; })()} />
        <Route path="/research/do-dynasties-repeat" element={(() => { const C = require("@/components/content/DynastiesRepeatResearch").default; return <C />; })()} />
        <Route path="/research/draft-picks-that-win-championships" element={(() => { const C = require("@/components/content/DraftPicksChampionshipsResearch").default; return <C />; })()} />
        <Route path="/research/when-players-lose-value-by-age" element={(() => { const C = require("@/components/content/WhenPlayersFallOffResearch").default; return <C />; })()} />
        <Route path="/research/how-long-dynasty-assets-hold-value" element={(() => { const C = require("@/components/content/HowLongValueResearch").default; return <C />; })()} />
        <Route path="/research/when-running-backs-fall-off" element={(() => { const C = require("@/components/content/RBCliffResearch").default; return <C />; })()} />
        <Route path="/research/why-elite-wrs-are-safest" element={(() => { const C = require("@/components/content/EliteWRSafestResearch").default; return <C />; })()} />
        <Route path="/research/superflex-qb-longevity" element={(() => { const C = require("@/components/content/QBLongevityResearch").default; return <C />; })()} />
      </Routes>
      <SiteFooter />
    </>;
  }

  // Billing accessible after Google login (no Sleeper needed)
  if (location.pathname.startsWith('/billing') && user && user.email) {
    return (
      <>
        <Navbar {...sharedProps} />
        <main className="pt-14 sm:pt-16">
          <Suspense fallback={<LazyFallback />}>
            <Routes>
              <Route path="/billing" element={<BillingPage user={user} />} />
              <Route path="/billing/success" element={<BillingPage user={user} />} />
            </Routes>
          </Suspense>
        </main>
        <SiteFooter />
      </>
    );
  }

  // Show landing page for new visitors
  if (showLanding && !sleeperReady && !user) {
    return <><LandingPage onExplore={() => setShowLanding(false)} /><SiteFooter /></>;
  }

  // Sleeper import flow
  if (!sleeperReady) {
    return <SleeperLogin user={user} onSleeperLinked={handleSleeperLinked} onGuestSleeper={handleGuestSleeper} />;
  }

  return (
    <>
      <Navbar {...sharedProps} />
      <OnboardingTour run={!!leagueData} />
      <main className="pt-14 sm:pt-16">
        <PromoBanner user={user} />
        <Routes>
          <Route path="/" element={
            loading && !leagueData ? (
              <div className="max-w-[1600px] mx-auto px-6 py-16">
                <div className="space-y-4">
                  <div className="h-10 w-64 skeleton rounded-sm" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="h-32 skeleton rounded-sm" />
                    <div className="h-32 skeleton rounded-sm" />
                    <div className="h-32 skeleton rounded-sm" />
                  </div>
                  <div className="h-64 skeleton rounded-sm" />
                </div>
              </div>
            ) : leagueData ? <ErrorBoundary><Dashboard {...sharedProps} /></ErrorBoundary> : <LeagueManager {...sharedProps} />
          } />
          <Route path="/billing" element={<BillingPage user={user} />} />
          <Route path="/billing/success" element={<BillingPage user={user} />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/email-preferences" element={<EmailPreferences user={user} />} />
          <Route path="/referral" element={<ReferralPage user={user} />} />
          <Route path="/api-keys" element={(() => { const ApiKeysPage = require("@/components/ApiKeysPage").default; return <ApiKeysPage user={user} />; })()} />
          <Route path="/trade" element={<Suspense fallback={<LazyFallback />}><TradeAnalyzer {...sharedProps} /></Suspense>} />
          <Route path="/calculator" element={<Suspense fallback={<LazyFallback />}><TradeCalculator {...sharedProps} /></Suspense>} />
          <Route path="/finder" element={<Suspense fallback={<LazyFallback />}><TradeFinder {...sharedProps} /></Suspense>} />
          <Route path="/target" element={<Suspense fallback={<LazyFallback />}><TargetPlayer {...sharedProps} /></Suspense>} />
          <Route path="/simulator" element={<Suspense fallback={<LazyFallback />}><TradeSimulator {...sharedProps} /></Suspense>} />
          <Route path="/inbox" element={<Suspense fallback={<LazyFallback />}><TradeInbox {...sharedProps} /></Suspense>} />
          <Route path="/compare" element={<Suspense fallback={<LazyFallback />}><PlayerCompare {...sharedProps} /></Suspense>} />
          <Route path="/draft-room" element={<Suspense fallback={<LazyFallback />}><DraftWarRoom {...sharedProps} /></Suspense>} />
          <Route path="/mock-draft" element={<Suspense fallback={<LazyFallback />}><MockDraft {...sharedProps} /></Suspense>} />
          <Route path="/optimizer" element={<Suspense fallback={<LazyFallback />}><RosterOptimizer {...sharedProps} /></Suspense>} />
          <Route path="/history" element={<Suspense fallback={<LazyFallback />}><LeagueHistory {...sharedProps} /></Suspense>} />
          <Route path="/evaluate" element={<Suspense fallback={<LazyFallback />}><TradeEvaluator {...sharedProps} /></Suspense>} />
          <Route path="/contender" element={<Suspense fallback={<LazyFallback />}><ContenderScore {...sharedProps} /></Suspense>} />
          <Route path="/admin" element={<Suspense fallback={<LazyFallback />}><AdminDashboard {...sharedProps} /></Suspense>} />
          <Route path="/admin/posts" element={<Suspense fallback={<LazyFallback />}>{(() => { const PostGenerator = require("@/components/PostGenerator").default; return <PostGenerator user={user} />; })()}</Suspense>} />
          <Route path="/admin/card-generator" element={(() => { const CardGenerator = require("@/components/CardGenerator").default; return <CardGenerator user={user} />; })()} />
          <Route path="/admin/ad-graphics" element={(() => { const AdGraphicsStudio = require("@/components/AdGraphicsStudio").default; return <AdGraphicsStudio user={user} />; })()} />
          <Route path="/admin/data-management" element={(() => { const DataManagement = require("@/components/DataManagement").default; return <DataManagement />; })()} />
          <Route path="/teams" element={<Suspense fallback={<LazyFallback />}><AllTeams {...sharedProps} /></Suspense>} />
          <Route path="/leagues" element={<LeagueManager {...sharedProps} />} />
          {/* Content pages accessible to logged-in users with app navbar */}
          <Route path="/guide" element={(() => { const GuidePage = require("@/components/GuidePage").default; return <GuidePage />; })()} />
          <Route path="/guide/superflex-strategy" element={(() => { const C = require("@/components/content/SuperflexGuide").default; return <C />; })()} />
          <Route path="/guide/sleeper-setup" element={(() => { const C = require("@/components/content/SleeperSetupGuide").default; return <C />; })()} />
          <Route path="/guide/startup-draft-strategy" element={(() => { const C = require("@/components/content/StartupDraftGuide").default; return <C />; })()} />
          <Route path="/guide/dynasty-startup-draft" element={(() => { const C = require("@/components/content/StartupDraftDataGuide").default; return <C />; })()} />
        <Route path="/guide/dynasty-trade-deadline" element={(() => { const C = require("@/components/content/TradeDeadlineGuide").default; return <C />; })()} />
        <Route path="/guide/dynasty-rookie-draft" element={(() => { const C = require("@/components/content/RookieDraftGuide").default; return <C />; })()} />
        <Route path="/guide/dynasty-rebuild" element={(() => { const C = require("@/components/content/RebuildGuide").default; return <C />; })()} />
          <Route path="/guide/dynasty-vs-redraft" element={(() => { const C = require("@/components/content/DynastyVsRedraft").default; return <C />; })()} />
          <Route path="/guide/:slug" element={(() => { const C = require("@/components/content/GuideArticle").default; return <C />; })()} />
          <Route path="/compare/dynasty-trade-value-tools" element={(() => { const C = require("@/components/content/TradeValueToolsComparison").default; return <C />; })()} />
          <Route path="/compare" element={(() => { const C = require("@/components/content/CompareIndex").default; return <C />; })()} />
          <Route path="/compare/:pair" element={(() => { const C = require("@/components/content/ComparisonPage").default; return <C />; })()} />
          <Route path="/rankings" element={(() => { const C = require("@/components/content/DynastyValueChart").default; return <C />; })()} />
          <Route path="/rookie-rankings" element={(() => { const C = require("@/components/content/RookieRankings").default; return <C />; })()} />
          <Route path="/trade-calculator" element={(() => { const C = require("@/components/PublicCalculator").default; return <C />; })()} />
          <Route path="/blog" element={(() => { const { BlogList } = require("@/components/BlogPage"); return <BlogList />; })()} />
          <Route path="/blog/:slug" element={(() => { const { BlogPost } = require("@/components/BlogPage"); return <BlogPost />; })()} />
          <Route path="/players" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
          <Route path="/players/qb" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
          <Route path="/players/rb" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
          <Route path="/players/wr" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
          <Route path="/players/te" element={(() => { const C = require("@/components/content/PlayersIndex").default; return <C />; })()} />
          <Route path="/players/:name" element={(() => { const C = require("@/components/content/PlayerPage").default; return <C />; })()} />
          <Route path="/championship-blueprint" element={(() => { const C = require("@/components/content/ChampionshipBlueprint").default; return <C />; })()} />
          <Route path="/research" element={(() => { const C = require("@/components/content/ResearchIndex").default; return <C />; })()} />
          <Route path="/research/championship-report" element={(() => { const C = require("@/components/content/ChampionshipReport").default; return <C />; })()} />
          <Route path="/research/qb-question" element={(() => { const { SpokeA_QBQuestion } = require("@/components/content/ChampionshipSpokes"); return <SpokeA_QBQuestion />; })()} />
          <Route path="/research/league-winners" element={(() => { const { SpokeB_LeagueWinners } = require("@/components/content/ChampionshipSpokes"); return <SpokeB_LeagueWinners />; })()} />
          <Route path="/research/stars-and-scrubs" element={(() => { const { SpokeC_StarsAndScrubs } = require("@/components/content/ChampionshipSpokes"); return <SpokeC_StarsAndScrubs />; })()} />
          <Route path="/research/age-of-champion" element={(() => { const { SpokeD_AgeOfChampion } = require("@/components/content/ChampionshipSpokes"); return <SpokeD_AgeOfChampion />; })()} />
          <Route path="/research/elite-te-question" element={(() => { const { SpokeF_EliteTE } = require("@/components/content/ChampionshipSpokes"); return <SpokeF_EliteTE />; })()} />
          <Route path="/research/positional-blueprint" element={(() => { const { SpokeG_PositionalBlueprint } = require("@/components/content/ChampionshipSpokes"); return <SpokeG_PositionalBlueprint />; })()} />
          <Route path="/trades" element={(() => { const { TradeExplorer } = require("@/components/content/TradeExplorer"); return <TradeExplorer />; })()} />
          <Route path="/trades/graded" element={(() => { const C = require("@/components/content/GradedTrades").default; return <C />; })()} />
          <Route path="/trades/:slug" element={(() => { const { PlayerTrades } = require("@/components/content/TradeExplorer"); return <PlayerTrades />; })()} />
          <Route path="/weekly/trades" element={(() => { const { WeeklyTrades } = require("@/components/content/WeeklyPulse"); return <WeeklyTrades />; })()} />
          <Route path="/weekly/waiver-wire" element={(() => { const { WaiverWireReport } = require("@/components/content/WeeklyPulse"); return <WaiverWireReport />; })()} />
          <Route path="/weekly/movers" element={(() => { const { ValueMovers } = require("@/components/content/WeeklyPulse"); return <ValueMovers />; })()} />
          <Route path="/tools/trade-fairness" element={(() => { const C = require("@/components/content/CollusionChecker").default; return <C />; })()} />
        <Route path="/tools/draft-archetype" element={(() => { const C = require("@/components/content/DraftArchetypeFinder").default; return <C />; })()} />
          <Route path="/research/how-lopsided-is-the-average-trade" element={(() => { const C = require("@/components/content/HowLopsidedResearch").default; return <C />; })()} />
          <Route path="/research/do-teams-that-trade-more-win" element={(() => { const C = require("@/components/content/TradeMoreWinResearch").default; return <C />; })()} />
          <Route path="/research/what-championship-rosters-have-in-common" element={(() => { const C = require("@/components/content/ChampionshipRostersResearch").default; return <C />; })()} />
          <Route path="/research/who-wins-lopsided-trades" element={(() => { const C = require("@/components/content/LopsidedTradesResearch").default; return <C />; })()} />
          <Route path="/research/what-winning-trades-look-like" element={(() => { const C = require("@/components/content/WinningTradesResearch").default; return <C />; })()} />
          <Route path="/research/how-champions-build-rosters" element={(() => { const C = require("@/components/content/ChampionsBuildResearch").default; return <C />; })()} />
          <Route path="/research/startup-strategies-that-win" element={(() => { const C = require("@/components/content/StartupStrategiesResearch").default; return <C />; })()} />
          <Route path="/research/how-long-a-rebuild-takes" element={(() => { const C = require("@/components/content/RebuildLengthResearch").default; return <C />; })()} />
          <Route path="/research/which-rookie-picks-hit" element={(() => { const C = require("@/components/content/RookieHitRatesResearch").default; return <C />; })()} />
          <Route path="/research/the-real-age-of-a-champion" element={(() => { const C = require("@/components/content/ChampionAgeResearch").default; return <C />; })()} />
          <Route path="/research/do-you-need-an-elite-qb" element={(() => { const C = require("@/components/content/EliteQBResearch").default; return <C />; })()} />
          <Route path="/research/fastest-way-out-of-a-rebuild" element={(() => { const C = require("@/components/content/FastestRebuildResearch").default; return <C />; })()} />
          <Route path="/research/stars-vs-depth" element={(() => { const C = require("@/components/content/StarsVsDepthResearch").default; return <C />; })()} />
          <Route path="/research/where-champions-put-their-value" element={(() => { const C = require("@/components/content/PositionalValueResearch").default; return <C />; })()} />
          <Route path="/research/championship-dna-players" element={(() => { const C = require("@/components/content/ChampionshipDNAResearch").default; return <C />; })()} />
          <Route path="/research/what-happens-in-startup-drafts" element={(() => { const C = require("@/components/content/StartupDraftsResearch").default; return <C />; })()} />
          <Route path="/research/how-many-studs-to-win" element={(() => { const C = require("@/components/content/HowManyStudsResearch").default; return <C />; })()} />
          <Route path="/research/do-champions-pay-up-at-tight-end" element={(() => { const C = require("@/components/content/TightEndResearch").default; return <C />; })()} />
          <Route path="/research/when-to-make-your-move" element={(() => { const C = require("@/components/content/TradeTimingResearch").default; return <C />; })()} />
          <Route path="/research/do-dynasties-repeat" element={(() => { const C = require("@/components/content/DynastiesRepeatResearch").default; return <C />; })()} />
          <Route path="/research/draft-picks-that-win-championships" element={(() => { const C = require("@/components/content/DraftPicksChampionshipsResearch").default; return <C />; })()} />
          <Route path="/research/when-players-lose-value-by-age" element={(() => { const C = require("@/components/content/WhenPlayersFallOffResearch").default; return <C />; })()} />
          <Route path="/research/how-long-dynasty-assets-hold-value" element={(() => { const C = require("@/components/content/HowLongValueResearch").default; return <C />; })()} />
          <Route path="/research/when-running-backs-fall-off" element={(() => { const C = require("@/components/content/RBCliffResearch").default; return <C />; })()} />
          <Route path="/research/why-elite-wrs-are-safest" element={(() => { const C = require("@/components/content/EliteWRSafestResearch").default; return <C />; })()} />
          <Route path="/research/superflex-qb-longevity" element={(() => { const C = require("@/components/content/QBLongevityResearch").default; return <C />; })()} />
          <Route path="/how-it-works" element={(() => { const C = require("@/components/HowItWorks").default; return <C />; })()} />
          <Route path="/features" element={(() => { const C = require("@/components/FeaturesPage").default; return <C />; })()} />
          <Route path="/faq" element={(() => { const C = require("@/components/FaqPage").default; return <C />; })()} />
          <Route path="/pricing" element={(() => { const C = require("@/components/PricingPage").default; return <C />; })()} />
        <Route path="/dynasty50" element={(() => { const C = require("@/components/PromoPage").default; return <C />; })()} />
          <Route path="/about" element={(() => { const C = require("@/components/content/AboutPage").default; return <C />; })()} />
          <Route path="/contact" element={(() => { const C = require("@/components/content/ContactPage").default; return <C />; })()} />
          <Route path="/sleeper-analyzer" element={(() => { const C = require("@/components/content/SleeperAnalyzerPage").default; return <C />; })()} />
          <Route path="/sitemap" element={(() => { const { SitemapIndex } = require("@/components/content/SitemapPages"); return <SitemapIndex />; })()} />
          <Route path="/sitemap/players" element={(() => { const { SitemapPlayers } = require("@/components/content/SitemapPages"); return <SitemapPlayers />; })()} />
          <Route path="/sitemap/trades" element={(() => { const { SitemapTrades } = require("@/components/content/SitemapPages"); return <SitemapTrades />; })()} />
          <Route path="/sitemap/compare" element={(() => { const { SitemapCompare } = require("@/components/content/SitemapPages"); return <SitemapCompare />; })()} />
          <Route path="/sitemap/compare/:page" element={(() => { const { SitemapCompare } = require("@/components/content/SitemapPages"); return <SitemapCompare />; })()} />
          <Route path="/unsubscribe" element={(() => { const C = require("@/components/content/UnsubscribePage").default; return <C />; })()} />
        <Route path="/report/:reportId" element={(() => { const C = require("@/components/SharedReport").default; return <C />; })()} />
        </Routes>
      </main>
      <SiteFooter />
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <BrowserRouter>
        <Routes>
          <Route path="/shared/:token" element={<SharedTrade />} />
          <Route path="*" element={<AppRouter />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
